import KogModalSideComponent from "@/main/components/modal/ModalSide";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import { KeyValueDefault } from "@/main/models/common";
import { KeyValueDefaultEnum } from "@/main/models/common/keyValueDefaultEnum";
import { QuoteGetItemsResponse } from "@/main/models/response";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { getDefaultCountryOptionDrop, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { convertStringForBoolean } from "@/main/services/utilsString";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getReason, useReasonReducer } from "@/main/store/reason";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import * as S from "./styles";
import { IBasePartsForm, SupplierPartsSelector } from "./supplierPartsSelector";

export interface IRejectPartsForm extends IBasePartsForm {
	reason: KeyValueDefaultEnum;
	reasonDescription: string | null;
}

interface IRejectPartsModal {
	isOpen: boolean;
	onClose: () => void;
	data: QuoteGetItemsResponse[];
	showPrices: boolean;
	onSubmitAction: (data: IRejectPartsForm) => Promise<void>;
}

function RejectPartsModalContent({
	onClose,
	data,
	showPrices,
	onSubmitAction,
}: Readonly<Omit<IRejectPartsModal, "isOpen">>) {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { reason: reasonReducer } = useReasonReducer();
	const { countriesUser: countryReducer } = useCountryReducer();
	const dispatch = useDispatch();

	const methods = useForm<IRejectPartsForm>({
		defaultValues: {
			quoteSupplierItemGuids: [],
			reasonDescription: "",
		},
	});

	const watcherReasonType = methods.watch("reason");
	const isReasonOther = convertStringForBoolean(watcherReasonType?.default);

	useEffect(() => {
		if (data && data.length > 0) {
			methods.reset({
				quoteSupplierItemGuids: [],
				reasonDescription: "",
			});
		}
	}, [data, methods]);

	useEffect(() => {
		dispatch(getCountriesUser());
	}, []);

	useEffect(() => {
		const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
		if (countryUser === null) return;

		dispatch(
			getReason({
				idReasonType: ReasonTypeEnum.RejectPart,
				guidCountry: String(countryUser.key),
			}),
		);
	}, [JSON.stringify(countryReducer.data)]);

	function submit(formData: IRejectPartsForm) {
		if (isNullUndefined(formData.reason)) {
			toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.reason").toLowerCase(),
				),
			);
			return;
		}

		if (formData.quoteSupplierItemGuids.length === 0) {
			toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.part").toLowerCase(),
				),
			);
			return;
		}
		setIsLoading(true);
		onSubmitAction(formData).finally(() => {
			setIsLoading(false);
		});
	}

	const selectedGuids = methods.watch("quoteSupplierItemGuids") || [];

	const isDisabled = selectedGuids.length === 0;

	return (
		<FormProvider {...methods}>
			<form noValidate>
				<S.ContainerModal>
					<SupplierPartsSelector data={data} showPrices={showPrices} />
					<S.AreaFooterReject>
						<S.AreaReason>
							<KogAutoCompleteControlled
								id="select-reason-reject"
								nameCustom="reason"
								isDefaultValueUnique
								label={getTranslateByKey("information.reason")}
								control={methods.control as any}
								rulesCustom={{ required: true }}
								setValueCustom={methods.setValue as any}
								options={reasonReducer.data}
								isLoading={reasonReducer.isLoading}
								onChangeCustom={(option) => {
									if (option !== null && convertStringForBoolean((option as KeyValueDefault)?.default)) {
										methods.setValue("reasonDescription", "");
									}
								}}
							/>
							{isReasonOther && (
								<KogInputTextControlledNew
									required={isReasonOther}
									id="input-description-reject"
									label={getTranslateByKey("information.description")}
									name="reasonDescription"
									control={methods.control as any}
								/>
							)}
						</S.AreaReason>

						<S.AreaButtons>
							<SecondaryButton type="button" onClick={onClose}>
								{getTranslateByKey("information.back")}
							</SecondaryButton>
							<Button onClick={methods.handleSubmit(submit)} loading={isLoading} disabled={isDisabled}>
								{getTranslateByKey("information.reject")}
							</Button>
						</S.AreaButtons>
					</S.AreaFooterReject>
				</S.ContainerModal>
			</form>
		</FormProvider>
	);
}

export default function RejectPartsModal(props: Readonly<IRejectPartsModal>) {
	return (
		<KogModalSideComponent
			shouldRenderOpen
			title={getTranslateByKey("information.rejectedParts")}
			open={props.isOpen}
			closeModal={props.onClose}
		>
			{props.isOpen && <RejectPartsModalContent {...props} />}
		</KogModalSideComponent>
	);
}
